'use client'

import * as React from 'react'
import { FacilitatorImageCarousel } from './FacilitatorImageCarousel'
import { ExpandableText } from './ExpandableText'
import { Button } from '~/components/ui/button'
import { Edit } from 'lucide-react'

// #1174 FR-6 — this used to be an inline editor: isEditMode/setIsEditMode
// and the save/upload/cancel handlers around it. Nothing ever called
// setIsEditMode(true), so that whole path was dead — and had it run, its
// save button posted the bio to the sign-up flow's STEP3_QUALIFICATIONS
// endpoint rather than the facilitator profile, which would have silently
// dropped images and bio edits. Only the read-only display these components
// were always actually rendering is kept; editing lives at
// /hosting/profile/edit.

type EditorContextType = {
  shortBio: string
  wellnessImages: string[]
}

const EditorContext = React.createContext<EditorContextType | null>(null)

export function FacilitatorProfileEditorProvider({
  initialShortBio = '',
  initialWellnessImages = [],
  children,
}: {
  facilitatorId: string
  initialShortBio?: string
  initialWellnessImages: string[]
  children: React.ReactNode
}) {
  const value: EditorContextType = {
    shortBio: initialShortBio,
    wellnessImages: initialWellnessImages,
  }

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>
}

export function useEditor() {
  const context = React.useContext(EditorContext)
  if (!context) {
    throw new Error('useEditor must be used within FacilitatorProfileEditorProvider')
  }
  return context
}

// Defaults to the owner's own edit page; callers pass `href` to point an admin
// at the admin edit page for the profile being viewed (#18 — the button must
// never silently edit a different profile than the one on screen).
export function FacilitatorEditButton({ href = '/hosting/profile/edit' }: { href?: string }) {
  return (
    <a href={href}>
      <Button
        variant="outline"
        size="sm"
        className="rounded-full border-[#D9CDB9] bg-[#FFFDF8] font-bold text-[#2C2D2B] shadow-none hover:bg-[#F7F0E6]"
      >
        <Edit className="h-4 w-4 mr-2" />
        Edit
      </Button>
    </a>
  )
}

export function FacilitatorImageSection({ facilitatorName }: { facilitatorName: string }) {
  const { wellnessImages } = useEditor()

  return (
    <FacilitatorImageCarousel
      images={wellnessImages}
      alt={`${facilitatorName} wellness images`}
    />
  )
}

export function FacilitatorAboutMeSection({ defaultBio }: { defaultBio?: string }) {
  const { shortBio } = useEditor()
  const displayText = shortBio || defaultBio || 'No bio available.'

  return (
    <ExpandableText text={displayText} maxLines={4} />
  )
}
